/**
 * Access-method detail page. Centrepiece is the **chain visualisation**: each
 * layer of the composed access method rendered as a card from outer (top) to
 * leaf (bottom), with the resolved command shape between them. This is the
 * single best way to convince an operator "yes, you've described what you
 * think you've described".
 *
 * The "Test connection" button is intentionally stubbed for this round —
 * real ssh2 wiring lands in a focused follow-up so this round stays small.
 * The button records a "would-have-tested" audit entry and explains the
 * stub state so users aren't surprised.
 */
import {
  Network,
  Loader2,
  Pencil,
  Trash2,
  Save,
  X,
  ChevronLeft,
  Terminal,
  Container,
  Layers as LayersIcon,
  Cpu,
  ArrowDown,
  PlugZap,
  KeyRound,
  Info,
  CheckCircle2,
  XCircle,
  Clock,
} from 'lucide-react';
import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from '@tanstack/react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/Card.js';
import { Button } from '@/components/ui/Button.js';
import { Input } from '@/components/ui/Input.js';
import { Label } from '@/components/ui/Label.js';
import {
  api,
  ApiError,
  type AccessMethodKind,
  type AccessMethodTestResult,
  type AccessMethodWithChain,
  type UpdateAccessMethodInput,
} from '@/api/client.js';
import { KindBadge } from './AccessMethods.js';
import { SecretPicker } from '@/components/applications/SecretPicker.js';
import { cn } from '@/lib/utils.js';

const KIND_ICON: Record<AccessMethodKind, typeof Terminal> = {
  ssh: Terminal,
  docker: Container,
  kubectl: LayersIcon,
  local: Cpu,
};

export function AccessMethodDetailPage(): JSX.Element {
  const { id } = useParams({ strict: false }) as { id?: string };
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: ['access-method', id],
    queryFn: () => api.accessMethods.get(id!),
    enabled: !!id,
  });
  const am = query.data;

  const [editing, setEditing] = useState(false);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [credentialsRef, setCredentialsRef] = useState('');
  const [settingsText, setSettingsText] = useState('{}');
  const [settingsError, setSettingsError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState<AccessMethodTestResult | null>(null);
  const [testError, setTestError] = useState<string | null>(null);

  useEffect(() => {
    if (!am) return;
    setName(am.name);
    setDescription(am.description ?? '');
    setCredentialsRef(am.credentialsRef ?? '');
    setSettingsText(JSON.stringify(am.settings ?? {}, null, 2));
  }, [am]);

  async function save() {
    if (!am) return;
    setSaving(true);
    setSaveError(null);
    let parsedSettings: Record<string, unknown>;
    try {
      parsedSettings = settingsText.trim() ? JSON.parse(settingsText) : {};
      setSettingsError(null);
    } catch (err) {
      setSettingsError(err instanceof Error ? err.message : 'Invalid JSON');
      setSaving(false);
      return;
    }
    const patch: UpdateAccessMethodInput = {
      name,
      description: description.length > 0 ? description : null,
      credentialsRef: credentialsRef.length > 0 ? credentialsRef : null,
      settings: parsedSettings,
    };
    try {
      await api.accessMethods.update(am.id, patch);
      toast.success('Access method saved');
      await queryClient.invalidateQueries({ queryKey: ['access-method', am.id] });
      await queryClient.invalidateQueries({ queryKey: ['access-methods'] });
      setEditing(false);
    } catch (err) {
      setSaveError(err instanceof ApiError ? err.message : 'Could not save changes.');
    } finally {
      setSaving(false);
    }
  }

  async function destroy() {
    if (!am) return;
    if (!confirm(`Delete ${am.name}? This cannot be undone.`)) return;
    try {
      await api.accessMethods.delete(am.id);
      toast.success(`Deleted ${am.name}`);
      await queryClient.invalidateQueries({ queryKey: ['access-methods'] });
      await navigate({ to: '/p/applications/access-methods' });
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Could not delete.');
    }
  }

  async function testConnection() {
    if (!am) return;
    setTesting(true);
    setTestError(null);
    setTestResult(null);
    try {
      const result = await api.accessMethods.test(am.id);
      setTestResult(result);
      if (result.success) {
        toast.success(`Connection OK (${result.durationMs}ms)`);
      } else {
        toast.error(`Connection failed${result.errorCode ? ` — ${result.errorCode}` : ''}`);
      }
    } catch (err) {
      // 4xx from the API (e.g. "no credentialsRef set") — surfaced inline as
      // an error rather than as a fake result panel.
      const msg = err instanceof ApiError ? err.message : 'Could not run test';
      setTestError(msg);
      toast.error(msg);
    } finally {
      setTesting(false);
    }
  }

  if (query.isLoading) {
    return (
      <div className="flex justify-center py-12 text-muted-foreground">
        <Loader2 className="h-5 w-5 animate-spin" />
      </div>
    );
  }

  if (!am) {
    return (
      <Card>
        <CardContent className="py-10 text-center text-sm">
          <Network className="mx-auto mb-3 h-8 w-8 text-muted-foreground" />
          <p>Access method not found, or you don't have access to it.</p>
          <Button variant="link" onClick={() => navigate({ to: '/p/applications/access-methods' })}>
            Back to access methods
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-8">
      <div>
        <Link
          to="/p/applications/access-methods"
          className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
        >
          <ChevronLeft className="h-3 w-3" />
          All access methods
        </Link>
      </div>

      <header className="flex items-start justify-between gap-4">
        <div className="flex items-start gap-4">
          <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-primary/10 text-primary">
            <Network className="h-6 w-6" />
          </div>
          <div className="space-y-1">
            {editing ? (
              <Input value={name} onChange={(e) => setName(e.target.value)} className="text-xl font-semibold" />
            ) : (
              <h1 className="text-2xl font-semibold tracking-tight">{am.name}</h1>
            )}
            <div className="flex flex-wrap items-center gap-2 text-sm text-muted-foreground">
              <KindBadge kind={am.kind} />
              <Link
                to="/orgs/$slug"
                params={{ slug: am.orgSlug }}
                className="inline-flex items-center gap-1 hover:text-foreground"
              >
                {am.orgName}
              </Link>
              <span>·</span>
              <span className="font-mono">/{am.slug}</span>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {editing ? (
            <>
              <Button variant="ghost" onClick={() => setEditing(false)}>
                <X className="h-4 w-4" />
                Cancel
              </Button>
              <Button onClick={save} disabled={saving || !name}>
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                Save
              </Button>
            </>
          ) : (
            <>
              <Button variant="outline" onClick={() => setEditing(true)}>
                <Pencil className="h-4 w-4" />
                Edit
              </Button>
              <Button variant="ghost" onClick={destroy}>
                <Trash2 className="h-4 w-4" />
                Delete
              </Button>
            </>
          )}
        </div>
      </header>

      {saveError && (
        <p className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive">
          {saveError}
        </p>
      )}

      <Card>
        <CardHeader>
          <CardTitle>Resolved chain</CardTitle>
          <CardDescription>
            The full composition, outer-first. The leaf at the bottom is the layer that opens the
            actual network connection.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <ChainView head={am} />
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex flex-row items-start justify-between space-y-0">
          <div>
            <CardTitle className="flex items-center gap-2">
              <PlugZap className="h-4 w-4" />
              Test connection
            </CardTitle>
            <CardDescription>
              Opens the chain and runs <span className="font-mono">echo deployit-test-ok</span> through
              every layer. Auditable on every run.
            </CardDescription>
          </div>
          <Button variant="outline" onClick={testConnection} disabled={testing}>
            {testing ? <Loader2 className="h-4 w-4 animate-spin" /> : <PlugZap className="h-4 w-4" />}
            {testing ? 'Testing…' : 'Test'}
          </Button>
        </CardHeader>
        {(testResult || testError) && (
          <CardContent>
            {testError ? (
              <p className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive">
                {testError}
              </p>
            ) : testResult ? (
              <TestResultPanel result={testResult} />
            ) : null}
          </CardContent>
        )}
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Settings</CardTitle>
          <CardDescription>
            Kind-specific JSON. The server validates against the kind's schema on save.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-2">
          {editing ? (
            <textarea
              value={settingsText}
              onChange={(e) => setSettingsText(e.target.value)}
              rows={8}
              className="w-full rounded-md border border-input bg-background px-3 py-2 font-mono text-[11px] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            />
          ) : (
            <pre className="overflow-x-auto rounded-md border border-border bg-muted/30 p-3 font-mono text-[11px]">
              {JSON.stringify(am.settings ?? {}, null, 2)}
            </pre>
          )}
          {settingsError && <p className="text-xs text-destructive">{settingsError}</p>}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <KeyRound className="h-4 w-4" />
            Credentials
          </CardTitle>
          <CardDescription>
            Reference into the secrets table. The leaf typically carries the credential; outer
            layers usually carry none of their own.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-2">
          {editing ? (
            <SecretPicker orgId={am.orgId} value={credentialsRef} onChange={setCredentialsRef} />
          ) : am.credentialsRef ? (
            <span className="rounded-md bg-muted px-2 py-0.5 font-mono text-xs">{am.credentialsRef}</span>
          ) : (
            <p className="text-xs italic text-muted-foreground">No credential reference set.</p>
          )}
          <p className="rounded-md border border-border bg-muted/30 px-3 py-2 text-[11px] text-muted-foreground">
            <Info className="mr-1 inline h-3 w-3" />
            The leaf typically carries the credential; outer layers usually carry none of their
            own. Manage secrets on the Secrets page.
          </p>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>About</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {editing ? (
            <Input
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="(optional description)"
            />
          ) : (
            <p className="text-sm text-muted-foreground">
              {am.description ?? <span className="italic">No description.</span>}
            </p>
          )}
          <dl className="grid grid-cols-2 gap-x-4 gap-y-2 text-xs">
            <Row label="ID" value={am.id} mono />
            <Row label="Created" value={new Date(am.createdAt).toLocaleString()} />
            <Row label="Updated" value={new Date(am.updatedAt).toLocaleString()} />
          </dl>
        </CardContent>
      </Card>
    </div>
  );
}

function ChainView({ head }: { head: AccessMethodWithChain }): JSX.Element {
  // Walk the linked list into an array so we can render the arrows between layers.
  const layers: AccessMethodWithChain[] = [];
  let cursor: AccessMethodWithChain | null = head;
  while (cursor) {
    layers.push(cursor);
    cursor = cursor.transport;
  }
  return (
    <ol className="space-y-3">
      {layers.map((layer, i) => {
        const Icon = KIND_ICON[layer.kind];
        const isLeaf = i === layers.length - 1;
        return (
          <li key={layer.id} className="space-y-2">
            <div
              className={cn(
                'flex items-start gap-3 rounded-md border bg-card px-3 py-2',
                isLeaf ? 'border-primary/60' : 'border-border',
              )}
            >
              <div className="flex h-8 w-8 items-center justify-center rounded-md bg-primary/10 text-primary">
                <Icon className="h-4 w-4" />
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <span className="font-medium">{layer.name}</span>
                  <KindBadge kind={layer.kind} />
                  {isLeaf && (
                    <span className="rounded-full bg-primary/10 px-1.5 py-0.5 text-[10px] uppercase tracking-wider text-primary">
                      leaf
                    </span>
                  )}
                </div>
                <p className="mt-0.5 font-mono text-[11px] text-muted-foreground">
                  {summary(layer)}
                </p>
              </div>
              <Link
                to="/p/applications/access-methods/$id"
                params={{ id: layer.id }}
                search={{ org: layer.orgSlug }}
                className="text-xs text-muted-foreground hover:text-foreground"
              >
                Open
              </Link>
            </div>
            {!isLeaf && (
              <div className="flex justify-center">
                <ArrowDown className="h-3 w-3 text-muted-foreground" />
              </div>
            )}
          </li>
        );
      })}
    </ol>
  );
}

function summary(am: AccessMethodWithChain): string {
  const s = am.settings ?? {};
  switch (am.kind) {
    case 'ssh':
      return `ssh ${s['user'] ?? '?'}@${s['host'] ?? '?'}${s['port'] && s['port'] !== 22 ? ` -p ${s['port']}` : ''}`;
    case 'docker':
      return `docker exec ${s['container'] ?? '?'}${s['user'] ? ` -u ${s['user']}` : ''}`;
    case 'kubectl':
      return `kubectl --context=${s['context'] ?? '?'}${s['namespace'] ? ` -n ${s['namespace']}` : ''}${s['pod'] ? ` exec ${s['pod']}` : ''}${s['container'] ? ` -c ${s['container']}` : ''}`;
    case 'local':
      return 'local on DeployIt host';
  }
}

function Row({ label, value, mono = false }: { label: string; value: string; mono?: boolean }) {
  return (
    <>
      <dt className="text-muted-foreground">{label}</dt>
      <dd className={mono ? 'font-mono' : undefined}>{value}</dd>
    </>
  );
}

function TestResultPanel({ result }: { result: AccessMethodTestResult }): JSX.Element {
  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2 text-sm">
        {result.success ? (
          <span className="inline-flex items-center gap-1.5 rounded-md bg-emerald-500/10 px-2 py-1 font-medium text-emerald-700 dark:text-emerald-300">
            <CheckCircle2 className="h-3.5 w-3.5" />
            Connection OK
          </span>
        ) : (
          <span className="inline-flex items-center gap-1.5 rounded-md bg-destructive/10 px-2 py-1 font-medium text-destructive">
            <XCircle className="h-3.5 w-3.5" />
            Failed{result.errorCode ? ` — ${result.errorCode}` : ''}
          </span>
        )}
        <span className="inline-flex items-center gap-1 rounded-md bg-muted px-2 py-1 text-xs text-muted-foreground">
          <Clock className="h-3 w-3" />
          {result.durationMs}ms
        </span>
        {result.exitCode !== null && (
          <span className="inline-flex items-center gap-1 rounded-md bg-muted px-2 py-1 text-xs text-muted-foreground">
            exit {result.exitCode}
          </span>
        )}
      </div>

      <dl className="grid grid-cols-[max-content_1fr] gap-x-3 gap-y-1.5 text-xs">
        <dt className="text-muted-foreground">Chain</dt>
        <dd className="font-mono">{result.chainSummary}</dd>
        <dt className="text-muted-foreground">Resolved cmd</dt>
        <dd className="break-all font-mono">{result.resolvedCommand}</dd>
        {result.hostFingerprint && (
          <>
            <dt className="text-muted-foreground">Host key</dt>
            <dd className="break-all font-mono">{result.hostFingerprint}</dd>
          </>
        )}
      </dl>

      {result.stdout && (
        <div className="space-y-1">
          <div className="text-xs text-muted-foreground">stdout</div>
          <pre className="overflow-x-auto rounded-md border border-border bg-muted/30 p-3 font-mono text-[11px] text-foreground">
            {result.stdout.trimEnd()}
          </pre>
        </div>
      )}
      {result.stderr && (
        <div className="space-y-1">
          <div className="text-xs text-muted-foreground">stderr</div>
          <pre className="overflow-x-auto rounded-md border border-destructive/30 bg-destructive/5 p-3 font-mono text-[11px] text-destructive">
            {result.stderr.trimEnd()}
          </pre>
        </div>
      )}
    </div>
  );
}
